import useAxios from "@/hooks/useAxios";
import { useProjectStore } from "@/stores/projectStore";
import { ApiModules } from "@/types/enums/api-modules";
import { Member, Project, Task } from "@/types/project";
import { getFileType } from "@/utils/helpers";
import { AxiosError } from "axios";
import { useState } from "react";
import { ApiErrorResponse } from "@/types/response";

export const useGetAllProjects = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.PROJET);
  const { setProjects } = useProjectStore();

  const getAllProjects = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<Project[]>("/projects/");
      if (data) {
        setProjects(data);
        return { success: true, data };
      } else {
        throw new Error("Problem with fetching the projects from server");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  return { loading, getAllProjects };
};

export const useCreateProject = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.PROJET);

  const createProject = async (newProject: Project) => {
    try {
      setLoading(true);
      const { data } = await axios.post<any>("/projects/", {
        ...newProject,
      });
      if (data) {
        console.log(data);
        return { success: true, data };
      } else {
        throw new Error("Unable to create project");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  return { loading, createProject };
};

export const useUpdateProject = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.PROJET);
  const { selectedProject } = useProjectStore();

  const updateProjectObjectives = async (newProject: Project) => {
    const { title, description, deadline, start_date, location, objectives } =
      newProject;
    try {
      setLoading(true);
      // console.log(newObjectives?.join(","));
      const { data } = await axios.put<any>(
        `/projects/${selectedProject?.id}/`,
        {
          title,
          deadline,
          start_date,
          location,
          description,
          objectives,
        },
      );
      if (data) {
        console.log(data);
        return { success: true, data };
      } else {
        throw new Error("Unable to create project");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  return { loading, updateProjectObjectives };
};

export const useSetProjectFavorite = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.PROJET);
  const { selectedProject } = useProjectStore();

  const setProjectFavorite = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post<any>(
        `/projects/${selectedProject?.id}/update/favorite`,
      );
      if (data) {
        console.log(data);
        return { success: true, data };
      } else {
        throw new Error("Unable to update favorite status");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  return { loading, setProjectFavorite };
};

// Project Tasks API
export const useCreateProjectTask = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.PROJET);
  const { selectedProject } = useProjectStore();

  const createProjectTask = async (newProjectTask: Task) => {
    try {
      setLoading(true);
      const { data } = await axios.post<any>(
        `/projects/${selectedProject?.id}/tasks/`,
        {
          ...newProjectTask,
        },
      );
      if (data) {
        console.log(data);
        return { success: true, data };
      } else {
        throw new Error("Unable to create task");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  return { loading, createProjectTask };
};

// Project membemrs API
export const useGetAllProjectMembers = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.PROJET);
  const { setSelectedProjectMembers, selectedProject } = useProjectStore();

  const getAllProjectMembers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get<any>(
        `/projects/${selectedProject?.id}/members/`,
      );
      if (data) {
        setSelectedProjectMembers(data.members);
        return { success: true, data: data.members };
      } else {
        throw new Error(
          "Problem with fetching the projects members from server",
        );
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  return { loading, getAllProjectMembers };
};

export const useCreateProjectMember = () => {
  const { axios } = useAxios(ApiModules.PROJET);
  const { selectedProject } = useProjectStore();

  const createProjectMembers = async (
    newMembers: Member[],
  ): Promise<{
    success: boolean;
    failedMembers: string[];
    error?: AxiosError<ApiErrorResponse>;
  }> => {
    if (newMembers.length === 0) return { success: false, failedMembers: [] };

    // Execute all API requests
    const results = await Promise.allSettled(
      newMembers.map((member) =>
        axios.post(`/projects/${selectedProject?.id}/add_member/`, {
          email: member.user_email,
          role: "collaborator", // Assuming a default role, you can modify this as needed
          // You can also pass the member's role if needed
          // For example, if the member object has a role property, you can use:
          // role: member.role,
        }),
      ),
    );

    // Identify failed requests
    const failedMembers = results
      .map((result, index) =>
        result.status === "rejected" ? newMembers[index].name : null,
      )
      .filter(Boolean) as string[];

    // If all failed or if we want to return the last error
    let lastError: AxiosError<ApiErrorResponse> | undefined;
    if (failedMembers.length > 0) {
      const rejected = results.find(
        (r) => r.status === "rejected",
      ) as PromiseRejectedResult;
      if (rejected) lastError = rejected.reason as AxiosError<ApiErrorResponse>;
    }

    // Return success status and failed members
    return {
      success: failedMembers.length === 0,
      failedMembers,
      error: lastError,
    };
  };

  return { createProjectMembers };
};

export const useDeleteProjectMember = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.PROJET);
  const { selectedProject } = useProjectStore();

  const deleteProjectMember = async (currentMember: Member) => {
    try {
      setLoading(true);
      const { data } = await axios.delete<any>(
        `/projects/${selectedProject?.id}/remove_member/?email=${currentMember.user_email || currentMember.email}`,
      );

      if (data) {
        console.log(data);
        return { success: true, data };
      }
      return { success: true };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  return { loading, deleteProjectMember };
};

//Project Documents API
export const useUploadProjectDocument = () => {
  const { axios } = useAxios(ApiModules.PROJET);
  const { selectedProject } = useProjectStore();

  const uploadProjectDocument = async (
    newDocument: FileList,
  ): Promise<{ success: boolean; failedDocs: string[] }> => {
    if (newDocument.length === 0) return { success: false, failedDocs: [] };
    // Execute all API requests

    const results = await Promise.allSettled(
      Array.from(newDocument).map((doc) =>
        axios.post<any>(
          `/projects/${selectedProject?.id}/documents/`,
          {
            title: doc.name,
            file: doc,
            document_type: getFileType(doc.name),
          },
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        ),
      ),
    );

    // Identify failed requests
    const failedDocs = results
      .map((result, index) =>
        result.status === "rejected" ? newDocument[index].name : null,
      )
      .filter(Boolean) as string[];

    // Return success status and failed members
    return { success: failedDocs.length === 0, failedDocs };
  };

  return { uploadProjectDocument };
};

//Project Documents API
export const useGetAllProjectDocuments = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.PROJET);
  const { selectedProject } = useProjectStore();
  const { setSelectedProjectDocuments } = useProjectStore();

  const getAllProjectDocuments = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get<any>(
        `/projects/${selectedProject?.id}/documents/`,
      );
      if (data) {
        setSelectedProjectDocuments(data);
        return { success: true, data };
      } else {
        throw new Error("Unable to fetch documents");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };
  return { loading, getAllProjectDocuments };
};

// fetchProjects: async () => {
//   // const response:Project = await api.get('/projects');
//   //Mock data
//   setTimeout(() => {set({ projects: [{
//     id: "id1",
//     title: "title1"
//     subTitle: "subTitle1",
//     description: '"DataPulse" is a cutting-edge data platform designed to revolutionize data management and analytics. This comprehensive solution offers robust tools for collecting, organizing, and analyzing diverse data sets across multiple sources. With its user-friendly interface and powerful capabilities, DataPulse empowers businesses to make informed decisions through insightful data visualization, real-time monitoring, and seamless integration with existing systems. Whether handling structured or unstructured data, DataPulse ensures efficiency, accuracy, and scalability, unlocking the full potential of data-driven strategies for organizations across industries.',
//     status: 'active',
//     isFavorited: false,
//     createdAt: 'Jan 30, 2024',
//     deadLine: 'Feb 1, 2024',
//     documents: [
//       {id: '0',status: 'completed', title: 'Cahier de charge', downloadDate: 'Jan 1, 2024', lastModifiedDate: 'Jan 1, 2024', fileSize: '250 Mo', type: 'docx', subTitle: "Cahier tres chargé"},
//       {id: '1',status: 'completed', title: "Contenu de l'application", type: "docx", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '2',status: 'completed', title: "guide_de_style.docx", type: "docx", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '3',status: 'completed', title: "Résumé des revues de design.doc", type: "doc", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '4',status: 'completed', title: "palette de couleurs", type: "xlsx", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '5',status: 'completed', title: "Spécifications de conception.pptx", type: "pptx", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '6',status: 'completed', title: "Maquette_finale.psd", type: "psd", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '7',status: 'completed', title: "Bibliothèque_de_type_patterns_UI.ai", type: "ai", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '8',status: 'completed', title: "Esquisses en fil de fer.jpg", type: "jpg", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '9',status: 'completed', title: "ensemble_icone.jpg", type: "jpg", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '10',status: 'completed', title: "Diagramme_de_flux_utilisateur.pdf", type: "pdf", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//       {id: '11',status: 'completed', title: "Diagramme_de_flux_utilisateur.pdf", type: "pdf", downloadDate: "8 janvier 2023", lastModifiedDate: "15 février 2023", fileSize: "12.5 Mo" },
//     ],
//     objectives: ["Create a book", "Clean life and earth", "Be better"],
//     startDate: 'Jan 30, 2024',
//     type: 'Ecologie',
//     membres: [
//       {id: '0', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '1', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '2', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '3', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '4', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '5', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '6', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '7', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '8', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '9', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '10', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//       {id: '11', name: 'Roberto Decproi', profilePictureUrl: '', email: 'robertoDecpoi@gmail.com', lastActivityDate: 'Jan 15, 2020', addedDate: 'Nov 02, 2019'},
//     ]
//   }] })}, 2000);

//   ;
// },
// addProject: (project) =>
//   set((state) => ({ projects: [...state.projects, project] })),
// removeProject: (id) =>
//   set((state) => ({
//     projects: state.projects.filter((project) => project.id !== id),
//   })),
// favoriteProject: (id) => set((state) => ({
//     projects: state.projects.map((project) =>
//       project.id === id ? { ...project, isFavorited: !project.isFavorited } : project
//     ),
//   })),
// addFiles: (newDocuments: ProjectDocument[], id: string) =>
//   set((state) => ({
//     projects: state.projects.map((project) =>
//       project.id === id ? {...project, documents: [...project.documents, ...newDocuments]} : project)
//   })),
// addProjectObjective: (newObjectives: string[], id:string) => {
//   set((state) => ({
//   projects: state.projects.map((project) =>
//     project.id === id ? {...project, objectives: [...project.objectives, ...newObjectives]} : project)
// }))},
// addProjectMembre: (newMembres: Membre[], id:string) => {
//   set((state) => ({
//   projects: state.projects.map((project) =>
//     project.id === id ? {...project, membres: [...project.membres, ...newMembres]} : project)
// }))},
